import * as DialogPrimitive from '@radix-ui/react-dialog'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, TablePropertiesIcon } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import Count from '@/components/ui/Count.tsx'
import { Dot } from '@/assets/icons/dot.tsx'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useNewIssueDialogStore } from '@/routes/projectStages/issues/lib/stores/useNewIssueDialogStore.ts'
import { NavigationItemT } from '@/lib/types/navigationTypes.ts'

/*
Floating bottom sheet — modelled on Linear's iOS "more" menu. Previously
used the shared DialogContent, which hard-codes a full-height mobile modal
(h-[calc(100svh-...)]), so the popup ate the whole screen with mostly
empty space around a short list. That felt heavy for "pick an issue type".

This version:
  - Floats above the bottom tab bar with small side margins (mx-3), rounded
    top+bottom, soft shadow.
  - Auto-sizes to its content, capped at ~70vh with internal scroll.
  - Tiny drag-handle pill on top (iOS convention).
  - Subtle dim overlay so the backdrop is present but not dominant.

Intentionally bypasses ui/Dialog.tsx's `DialogContent` wrapper to avoid
retrofitting the full-screen mobile modal into a bottom sheet globally.
*/

type PropsT = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function BottomTabBarIssuesSheet({ open, onOpenChange }: PropsT) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const stageIri = useProjectStageStore(s => s.iri)
  const links = useProjectStageStore(s => s.projectStageIssuesLinks)
  const issueTypes = useProjectStageStore(s => s.issueTypes)
  const setCurrIssueType = useProjectStageStore(s => s.setCurrIssueType)
  const setDialogOpened = useNewIssueDialogStore(s => s.updateDialogState)
  const setDraftIssueId = useNewIssueDialogStore(s => s.setDraftIssueId)

  const archivePath = stageIri
    ? `${stageIri}/archive?order=DESC&sort_by=CREATED_AT&view=ROWS`
    : null

  const handleClose = () => onOpenChange(false)

  // Shortcut: jump to that type's list and pop the new-issue dialog. The
  // dialog host only mounts inside Issues.tsx, so we must navigate first;
  // the Zustand `dialogOpen` flag survives the route change and the dialog
  // opens once Issues.tsx hydrates. Same constraint as MobileFab.
  const handleCreateIssue = (link: NavigationItemT) => {
    if (!link.issueTypeIri || !link.link) return
    setCurrIssueType(link.issueTypeIri)
    navigate(link.link)
    // Clear any stale draftIssueId so the new-issue form doesn't
    // inherit a previous draft's attachments / title (see IssueDialog
    // safety net + MobileFab for the matching pattern).
    setDraftIssueId(null)
    setDialogOpened(true)
    onOpenChange(false)
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Subtle dim — enough to anchor attention on the sheet without
            turning the page dark. Linear uses an even lighter scrim. */}
        <DialogPrimitive.Overlay
          className={`fixed inset-0 z-[1040] bg-black/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`}
        />
        <DialogPrimitive.Content
          data-cy="bottom-tab-issues-sheet"
          onOpenAutoFocus={e => e.preventDefault()}
          className={cn(
            // Anchor near the bottom, above the tab bar + safe area. The
            // `max-h` cap lets short lists auto-shrink and long lists scroll.
            `fixed inset-x-3 z-[1050] mx-auto flex max-w-md flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl`,
            `bottom-[calc(var(--bottomTabBarHeight)+var(--safe-area-inset-bottom)+0.75rem)]`,
            `max-h-[70svh]`,
            `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
          )}
        >
          {/* Drag handle — iOS convention. Pure visual affordance; the sheet
              dismisses via overlay click or bottom-tab toggle, no drag yet. */}
          <div className={`flex justify-center pt-2`}>
            <div className={`h-1 w-10 rounded-full bg-muted-foreground/30`} />
          </div>

          <DialogPrimitive.Title className={`px-4 pb-2 pt-2 text-base font-semibold`}>
            {t('navigation.mobile.issues')}
          </DialogPrimitive.Title>

          <div className={`flex flex-col gap-0.5 overflow-y-auto px-2 pb-3`}>
            {archivePath && (
              <NavLink
                to={archivePath}
                onClick={handleClose}
                className={`flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-accent`}
              >
                <TablePropertiesIcon className={`size-4`} />
                <span className={`flex-1 text-sm font-medium`}>
                  {t('navigation.mobile.allIssues')}
                </span>
              </NavLink>
            )}

            {/* Static "Wg typu" section header — always expanded. The sheet
                is already ephemeral and short; an extra tap to reveal the
                type list doesn't earn its own affordance. */}
            <div
              className={`mt-1 px-2 py-1.5 text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground`}
            >
              {t('navigation.mobile.byType')}
            </div>

            <div className={cn(`flex flex-col gap-0.5`)}>
              {links.length === 0 && (
                <span className={`px-2 py-2 text-sm text-muted-foreground`}>—</span>
              )}
              {links.map(link => {
                const issueType = link.issueTypeIri
                  ? issueTypes.find(t => t['@id'] === link.issueTypeIri)
                  : null
                const canCreate = !!issueType?.currentUserCanCreate
                return (
                  <div
                    key={link.link ?? link.name}
                    className={`group flex items-center rounded-lg pr-1 hover:bg-accent`}
                  >
                    <NavLink
                      to={link.link ?? '#'}
                      onClick={handleClose}
                      className={`flex min-w-0 flex-1 items-center gap-3 px-2 py-2.5`}
                    >
                      <Dot className={`size-3.5`} fill={link.color} />
                      <span className={`flex-1 truncate text-sm`}>{link.name}</span>
                      {!!link.notifications && link.notifications > 0 && (
                        <Count quantity={link.notifications} variant="dark" className={`ml-0`} />
                      )}
                    </NavLink>
                    {canCreate && (
                      <button
                        type="button"
                        onClick={() => handleCreateIssue(link)}
                        aria-label={`${t('common.add')} ${link.name}`}
                        data-cy={`bottom-tab-issues-sheet-create-${issueType?.id ?? ''}`}
                        style={{ backgroundColor: link.color }}
                        className={`ml-2 flex size-7 shrink-0 items-center justify-center rounded-md text-white shadow-sm transition-transform active:scale-95`}
                      >
                        <Plus className={`size-4`} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
